import React, { useEffect, useState } from 'react';
import {
  Panel,
  Title,
  Label,
  TitleLevel,
  Table,
  TableColumn,
  Ui5CustomEvent,
  Button,
  TableRow,
  TableCell,
  CheckBox,
  CheckBoxDomRef,
  ButtonDesign
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/decline';
import { useTranslation } from 'react-i18next';
import { useStyles } from './DistributionPanel.jss';
import {
  Distribution,
  DistributionStatus,
  LanguageWithSupplierFragment,
  Maybe
} from 'generated/graphql';
import { useGetDate } from 'hooks/useGetDate';
import { hideInternalSupplierVendor } from 'utils/hideInternalSupplierVendor';

type ButtonType = 'select' | 'unselect';

type ButtonsState = {
  [buttonType in ButtonType]: boolean;
};

type LanguagesProp = Maybe<LanguageWithSupplierFragment>;

export type DistributionPanelProps = {
  distributions?: Distribution[];
  languages?: LanguagesProp[];
  fetching: boolean;
  editMode: boolean;
  isInternalUser: boolean;
  selectedLanguages: string[];
  resetedDistributions: string[];
  setSelectedLanguages: (value: string[]) => void;
  setResetedDistributions: (value: string[]) => void;
};

export const DistributionPanel = (props: DistributionPanelProps) => {
  const {
    languages,
    distributions,
    fetching,
    isInternalUser,
    selectedLanguages,
    resetedDistributions,
    setSelectedLanguages,
    setResetedDistributions
  } = props;
  const { t } = useTranslation();
  const classes = useStyles();

  const [areButtonsDisabled, setAreButtonsDisabled] = useState<ButtonsState>({
    select: false,
    unselect: true
  });
  const { getFormattedDateString } = useGetDate();
  const editMode = props.editMode && isInternalUser;

  useEffect(() => {
    changeButtonVisibility();
  }, [selectedLanguages]);

  const changeButtonVisibility = () => {
    if (languages && selectedLanguages.length === languages.length) {
      setAreButtonsDisabled({ select: true, unselect: false });
    } else if (selectedLanguages.length === 0) {
      setAreButtonsDisabled({ select: false, unselect: true });
    } else {
      setAreButtonsDisabled({ select: false, unselect: false });
    }
  };

  const isButtonDisabled = (button: ButtonType) => {
    if (editMode) {
      return areButtonsDisabled[button];
    }
    return true;
  };

  const selectAllLanguages = () => {
    if (languages) {
      setSelectedLanguages(languages.map((language) => language?.id ?? ''));
    }
  };

  const getTextColor = (status?: DistributionStatus) => {
    const isCompleted =
      status !== DistributionStatus.DRAFT && status !== DistributionStatus.PENDING;
    if (isCompleted) {
      return classes.completed;
    }
    // @ts-ignore
    return classes[status.toLowerCase()];
  };

  const handleCheckboxClick = (
    distribution: Distribution,
    event: Ui5CustomEvent<CheckBoxDomRef>
  ) => {
    const { checked } = event.target;
    if (checked) {
      setSelectedLanguages([...selectedLanguages, distribution.language.id]);
    } else {
      const selectedLanguagesFiltered = selectedLanguages.filter(
        (selectedLanguage) => selectedLanguage !== distribution.language.id
      );
      setSelectedLanguages(selectedLanguagesFiltered);
    }
  };

  const handleRemoveDistributionClick = (distribution: Distribution) => {
    setResetedDistributions([...resetedDistributions, distribution.id]);
    if (selectedLanguages.includes(distribution.language.id)) {
      const selectedLanguagesFiltered = selectedLanguages.filter(
        (language) => language !== distribution.language.id
      );
      setSelectedLanguages(selectedLanguagesFiltered);
    }
  };

  const isResetedDistribution = (id: string) => {
    return resetedDistributions.some((resetedDistribution) => resetedDistribution === id);
  };

  const getStatus = (distribution: Distribution) => {
    if (isResetedDistribution(distribution.id)) {
      return DistributionStatus.DRAFT;
    } else {
      return distribution.status;
    }
  };

  const isLanguageChecked = (languageId: string) => {
    return selectedLanguages.includes(languageId);
  };
  return (
    <>
      <div
        data-testid="display-query-panel-distribution"
        data-help-id="display-query-panel-distribution"
      >
        <Panel
          aria-labelledby="distribution-panel"
          header={
            <Title id="distribution-panel" level={TitleLevel.H4}>
              {t('distribution-panel-title')}
            </Title>
          }
        >
          <div className={classes.inputLine}>
            <Label
              id="distribution-panel-combobox-label"
              data-help-id="distribution-panel-combobox-label"
              showColon
            >
              {t('distribution-panel-combobox-label')}
            </Label>
            <div>
              <Button
                className={classes.marginLeft}
                disabled={isButtonDisabled('select')}
                onClick={selectAllLanguages}
              >
                {t('distribution-panel-select-all')}
              </Button>
              <Button
                className={classes.marginLeft}
                disabled={isButtonDisabled('unselect')}
                onClick={() => setSelectedLanguages([])}
              >
                {t('distribution-panel-unselect-all')}
              </Button>
            </div>
          </div>
          <Table
            data-testid="distribution-table"
            busy={fetching}
            noDataText={
              isInternalUser
                ? t('distribution-table-no-data-text')
                : t('distribution-table-no-data-text-external')
            }
            columns={
              <>
                <TableColumn className={classes.languageColumn}>
                  <span data-help-id="distribution-table-column-language">
                    {t('distribution-table-column-language')}
                  </span>
                </TableColumn>
                <TableColumn>
                  <span data-help-id="distribution-table-column-implementer-group">
                    {t('distribution-table-column-implementer-group')}
                  </span>
                </TableColumn>
                <TableColumn>
                  <span data-help-id="distribution-table-column-distribute-next-answer">
                    {t('distribution-table-column-distribute-next-answer')}
                  </span>
                </TableColumn>
                <TableColumn>
                  <span data-help-id="distribution-table-column-status">
                    {t('distribution-table-column-status')}
                  </span>
                </TableColumn>
                <TableColumn />
                <TableColumn>
                  <span data-help-id="distribution-table-column-updated-at">
                    {t('distribution-table-column-updated-at')}
                  </span>
                </TableColumn>
              </>
            }
          >
            {distributions?.map((distribution) => (
              <TableRow key={distribution.id}>
                <TableCell>{distribution.language.language}</TableCell>
                <TableCell data-testid={`distribution-implementer-${distribution.id}`}>
                  {hideInternalSupplierVendor(distribution.language.supplier)}
                </TableCell>
                <TableCell>
                  <CheckBox
                    data-testid={`distribution-checkbox-${distribution.id}`}
                    onChange={(event) => handleCheckboxClick(distribution, event)}
                    checked={isLanguageChecked(distribution.language.id)}
                    disabled={!editMode}
                  />
                </TableCell>
                <TableCell className={getTextColor(getStatus(distribution))}>
                  {getStatus(distribution) !== DistributionStatus.DRAFT &&
                    (getStatus(distribution) ? t(`DISTRIBUTION-${distribution.status}`) : '')}
                </TableCell>
                <TableCell>
                  <Button
                    data-testid={`distribution-reset-${distribution.id}`}
                    design={ButtonDesign.Transparent}
                    icon="decline"
                    tooltip={t('distribution-table-column-reset-tooltip')}
                    className={editMode ? classes.resetEdit : classes.resetNotEdit}
                    disabled={!editMode}
                    onClick={() => handleRemoveDistributionClick(distribution)}
                  ></Button>
                </TableCell>
                <TableCell>
                  {getStatus(distribution) !== DistributionStatus.DRAFT &&
                    getFormattedDateString(distribution.updatedAt)}
                </TableCell>
              </TableRow>
            ))}
          </Table>
        </Panel>
      </div>
    </>
  );
};
